import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ambassadorOffersApi } from "../services/ambassadorOffers";
import { ambassadorSettingsApi } from "../services/ambassadorSettings";
import type { FactorDef, OfferInputs, SavedOffer } from "../types/ambassadors";
import { calculate, DEFAULT_BUDGET_PCT, defaultInputs, forSave, inputsFrom, sameOffer, savedFrom } from "./admin-ambassadors/calc";
import RevenueBudget from "./admin-ambassadors/RevenueBudget";
import { dateTime } from "./admin-ambassadors/format";
import { DEFAULT_FACTORS, factorDefsFrom, withFactorDefs } from "./admin-ambassadors/score";
import OfferInputsPanel from "./admin-ambassadors/OfferInputsPanel";
import OfferList from "./admin-ambassadors/OfferList";
import OfferPresentation from "./admin-ambassadors/OfferPresentation";
import OfferResultsPanel from "./admin-ambassadors/OfferResultsPanel";
import "./admin-ambassadors/Ambassadors.css";

// Admin → Ambassadors: the Ambassador Calculator. We offer ~10 influential DJs
// a share of Request's platform revenue for a few years instead of equity or
// sponsorship fees; this sizes each offer from the DJ's reach and shows it in
// a meeting-ready Present mode. Internal modelling only — it pays nobody.
//
// Two views: the saved offers (OfferList), and one offer open in the
// calculator (inputs left, live results right). The open offer is a draft
// until Save; it is kept in sessionStorage, so switching to another admin tab
// mid-meeting and back loses nothing. Each saved offer is a snapshot of its own
// inputs, so changing the defaults for a new offer never changes an old one —
// including the Strategic Influence Score's shared factor names and weights.

interface Draft {
  /** The saved offer being edited; null for one not saved yet. */
  id: string | null;
  inputs: OfferInputs;
  /** The inputs as last saved (null: never saved) — for "Unsaved changes". */
  saved: OfferInputs | null;
  savedAt: string | null;
}

const DRAFT_KEY = "adm-ambassador-draft";

function loadDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return {
      id: typeof d.id === "string" ? d.id : null,
      inputs: inputsFrom(d.inputs),
      saved: d.saved ? inputsFrom(d.saved) : null,
      savedAt: typeof d.savedAt === "string" ? d.savedAt : null,
    };
  } catch {
    return null;
  }
}

function storeDraft(d: Draft | null) {
  try {
    if (d) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    else sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* private mode or full: the draft just isn't kept */
  }
}

const statusOf = (err: unknown): number | undefined => (err as { response?: { status?: number } })?.response?.status;
const msgOf = (err: unknown): string | undefined => (err as { response?: { data?: { msg?: string } } })?.response?.data?.msg;

export default function AdminAmbassadorsTab() {
  const [offers, setOffers] = useState<SavedOffer[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(loadDraft);
  const [presenting, setPresenting] = useState<OfferInputs | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  // Shared settings — the score factors' names and weights, and the company-wide
  // revenue-share budget; defaults until loaded.
  const [factorDefs, setFactorDefs] = useState<FactorDef[]>(DEFAULT_FACTORS);
  const [budgetPct, setBudgetPct] = useState(DEFAULT_BUDGET_PCT);

  const load = useCallback(() => {
    setLoadFailed(false);
    ambassadorOffersApi.list().then(
      (data) => setOffers((data?.items || []).map(savedFrom).filter((o): o is SavedOffer => o !== null)),
      () => setLoadFailed(true)
    );
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    ambassadorSettingsApi.get().then(
      (data) => {
        setFactorDefs(factorDefsFrom(data?.factors));
        if (typeof data?.budgetPct === "number") setBudgetPct(data.budgetPct);
      },
      () => undefined // keep the defaults; saving a setting says if the server is unreachable
    );
  }, []);
  useEffect(() => storeDraft(draft), [draft]);

  const results = useMemo(() => (draft ? calculate(draft.inputs) : null), [draft]);
  const presented = useMemo(() => (presenting ? calculate(presenting) : null), [presenting]);
  const closePresentation = useCallback(() => setPresenting(null), []);

  // A new offer nobody has touched isn't worth a "discard?" question.
  const dirty = !!draft && !sameOffer(draft.inputs, draft.saved ?? defaultInputs(factorDefs));

  const startDraft = (d: Draft) => {
    setSaveError("");
    setDraft(d);
  };

  const backToList = () => {
    if (dirty && !window.confirm("Leave without saving? Your changes to this offer will be lost.")) return;
    setSaveError("");
    setDraft(null);
  };

  // New shared factor names / weights: every new offer gets them, and so does
  // the open one (ratings kept by factor id) as an unsaved change.
  const saveFactorDefs = async (defs: FactorDef[]) => {
    await ambassadorSettingsApi.save({ factors: defs });
    setFactorDefs(defs);
    setDraft((d) => d && { ...d, inputs: { ...d.inputs, score: { factors: withFactorDefs(d.inputs.score.factors, defs) } } });
  };

  const saveBudget = async (pct: number) => {
    await ambassadorSettingsApi.save({ budgetPct: pct });
    setBudgetPct(pct);
  };

  const remember = (saved: SavedOffer) => setOffers((prev) => [saved, ...(prev || []).filter((o) => o._id !== saved._id)]);

  const save = async () => {
    if (!draft) return;
    const inputs = forSave(draft.inputs);
    const snapshot = calculate(inputs);
    setSaving(true);
    setSaveError("");
    try {
      let raw: unknown;
      try {
        raw = draft.id
          ? await ambassadorOffersApi.update(draft.id, inputs, snapshot)
          : await ambassadorOffersApi.create(inputs, snapshot);
      } catch (err) {
        // Deleted somewhere else since it was opened: keep the work as a new offer.
        if (!draft.id || statusOf(err) !== 404) throw err;
        raw = await ambassadorOffersApi.create(inputs, snapshot);
      }
      const saved = savedFrom(raw);
      if (!saved) throw new Error("Unexpected answer");
      remember(saved);
      // Typing that happened during the save stays (and shows as unsaved).
      setDraft((d) => d && { ...d, id: saved._id, saved: saved.inputs, savedAt: saved.updatedAt });
    } catch (err) {
      setSaveError(msgOf(err) || "Couldn’t save. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (o: SavedOffer) => {
    setBusyId(o._id);
    try {
      const inputs = forSave({ ...o.inputs, djName: `${o.inputs.djName} (copy)`.slice(0, 120) });
      const copy = savedFrom(await ambassadorOffersApi.create(inputs, calculate(inputs)));
      if (copy) remember(copy);
    } catch {
      window.alert("Couldn’t duplicate the offer. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (o: SavedOffer) => {
    if (!window.confirm(`Delete the offer for ${o.inputs.djName}? This can’t be undone.`)) return;
    setBusyId(o._id);
    try {
      await ambassadorOffersApi.remove(o._id);
      setOffers((prev) => (prev || []).filter((x) => x._id !== o._id));
    } catch (err) {
      // Already gone (deleted in another window): just drop the row.
      if (statusOf(err) === 404) setOffers((prev) => (prev || []).filter((x) => x._id !== o._id));
      else window.alert("Couldn’t delete the offer. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const presentation = presenting && presented && (
    <OfferPresentation inputs={presenting} results={presented} onClose={closePresentation} />
  );

  if (!draft || !results) {
    return (
      <section className="fade-in amb-tab">
        <OfferList
          offers={offers}
          loadFailed={loadFailed}
          busyId={busyId}
          onRetry={load}
          onNew={() => startDraft({ id: null, inputs: defaultInputs(factorDefs), saved: null, savedAt: null })}
          onOpen={(o) => startDraft({ id: o._id, inputs: o.inputs, saved: o.inputs, savedAt: o.updatedAt })}
          onPresent={(o) => setPresenting(o.inputs)}
          onDuplicate={duplicate}
          onDelete={remove}
        />
        {offers && offers.length > 0 && (
          <RevenueBudget others={offers.map((o) => o.inputs)} budgetPct={budgetPct} onSaveBudget={saveBudget} />
        )}
        {presentation}
      </section>
    );
  }

  const name = draft.inputs.djName.trim();
  const status = !draft.saved ? "Not saved yet" : dirty ? "Unsaved changes" : `Saved ${dateTime(draft.savedAt)}`;
  const blocked = results.issues.length > 0;

  return (
    <section className="fade-in amb-tab">
      <div className="amb-bar">
        <button type="button" className="adm-btn-ghost" onClick={backToList}>
          ← Saved offers
        </button>
        <div className="amb-bar-title">
          <h2>{name || "New offer"}</h2>
          <span className={dirty ? "amb-unsaved" : "muted-text"}>{status}</span>
        </div>
        <div className="amb-bar-actions">
          {saveError && <span className="error-text amb-save-error">{saveError}</span>}
          <button
            type="button"
            className="adm-btn-light amb-save-btn"
            disabled={saving || !name}
            title={name ? undefined : "Add the DJ’s name first"}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="adm-btn-green amb-present-btn"
            disabled={blocked}
            title={blocked ? `Still needed: ${results.issues.join(", ")}` : undefined}
            onClick={() => setPresenting(draft.inputs)}
          >
            Present Offer
          </button>
        </div>
      </div>
      <div className="amb-calc">
        <OfferInputsPanel
          inputs={draft.inputs}
          calculatedShares={results.periods.map((p) => p.calculatedPct)}
          strategic={{ score: results.strategicScore, multiplier: results.strategicMultiplier }}
          sharedDefs={factorDefs}
          onSaveDefs={saveFactorDefs}
          update={(fn) => setDraft((d) => d && { ...d, inputs: fn(d.inputs) })}
        />
        <OfferResultsPanel
          inputs={draft.inputs}
          results={results}
          others={offers && offers.filter((o) => o._id !== draft.id).map((o) => o.inputs)}
          budgetPct={budgetPct}
          onSaveBudget={saveBudget}
        />
      </div>
      {presentation}
    </section>
  );
}
